import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridRowParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { useUserDateFormatter } from '../../../../shared/hooks/useUserDateFormatter';
import { DIAM_GRID_PAGE_SIZE_OPTIONS } from '../../diamGridUtils';
import { useDiamGridPagination } from '../../hooks/useDiamGridPagination';
import { DiamDataGrid } from '../DiamDataGrid/DiamDataGrid';
import { DataGridContainer } from '../DiamDataGrid/styles';
import { DiamDataGridTabs } from '../DiamDataGrid/types';
import { getRoleDetailsPath } from '../RoleDetails/constants';
import { mapApiRoleToGridRole } from '../RoleDetails/mapApiRole';
import { ROLE_CATEGORIES } from '../RoleDetails/types';
import { buildRoleColumns } from './columns';
import { Role } from './types';
import { useRoles } from './useRoles';

interface RolesGridProps {
  tabs?: DiamDataGridTabs;
}

export function RolesGrid({ tabs }: RolesGridProps) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { formatDateTime } = useUserDateFormatter();
  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    handleServerFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = useDiamGridPagination({
    initialSortField: 'name',
    initialSortOrder: 'asc',
  });

  const rolesQuery = useRoles({
    page: pageIndex,
    pageSize,
    sortModel,
    filterModel,
  });

  const columns = useMemo(
    () => buildRoleColumns(formatDateTime),
    [formatDateTime],
  );

  const rows = useMemo(
    () => (rolesQuery.data?.elements ?? []).map(mapApiRoleToGridRole),
    [rolesQuery.data],
  );

  useEffect(() => {
    if (rolesQuery.isError) {
      enqueueSnackbar(
        getApiErrorMessage(rolesQuery.error, 'Failed to load roles'),
        { variant: 'error' },
      );
    }
  }, [enqueueSnackbar, rolesQuery.error, rolesQuery.isError]);

  function handleRowClick(params: GridRowParams<Role>) {
    navigate(getRoleDetailsPath(ROLE_CATEGORIES.predefined, params.row.id));
  }

  return (
    <DataGridContainer elevation={1}>
      <DiamDataGrid
        rows={rows}
        columns={columns}
        loading={rolesQuery.isPending || rolesQuery.isFetching}
        disableRowSelectionOnClick
        getRowClassName={() => 'clickable'}
        onRowClick={handleRowClick}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleServerFilterModelChange}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        rowCount={rolesQuery.data?.total ?? 0}
        pageSizeOptions={[...DIAM_GRID_PAGE_SIZE_OPTIONS]}
        searchPlaceholder="Search roles"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        tabs={tabs}
      />
    </DataGridContainer>
  );
}
